<template>
  <div class="layout--main main-vertical navbar-floating footer-static cp-layout">
    <!-- Même menu latéral que l'ERP (logo, éléments, repli), avec les pages du portail. -->
    <v-nav-menu :navMenuItems="menuItems" title="XYLO" parent=".layout--main" />

    <div id="content-area" :class="contentAreaClass">
      <div id="content-overlay" />

      <!-- Barre du haut : même habillage que l'ERP, sans ses modules internes. -->
      <div class="relative">
        <div class="vx-navbar-wrapper" :class="navbarWidthClass">
          <vs-navbar class="vx-navbar navbar-custom navbar-skelton" color="#fff">
            <feather-icon class="sm:inline-flex xl:hidden cursor-pointer p-2" icon="MenuIcon" @click.stop="showSidebar" />
            <div v-if="portal.me" class="cp-companies">
              <vs-chip v-for="c in portal.me.companies" :key="c.id" color="success" class="cp-company">{{ c.name }}</vs-chip>
            </div>
            <vs-spacer />
            <div v-if="portal.me" class="cp-user hidden sm:block">
              <div class="cp-user-name">{{ portal.me.username }}</div>
              <div class="cp-user-mail">{{ portal.me.email }}</div>
            </div>
            <vs-button v-if="!(portal.me && portal.me.preview)" type="border" size="small" icon-pack="feather" icon="icon-log-out" class="ml-4" @click="signOut">
              {{ t('sign_out') }}
            </vs-button>
          </vs-navbar>
        </div>
      </div>

      <div class="content-wrapper">
        <div class="router-view">
          <div class="router-content">
            <!-- Aperçu admin (?as=) : rappel que ce n'est pas son propre portail. -->
            <vs-alert v-if="portal.me && portal.me.preview" color="warning" icon-pack="feather" icon="icon-eye" active="true" class="mb-4">
              Preview of the client portal of <strong>{{ portal.me.username }}</strong>, exactly what the client sees.
              <router-link :to="{ name: 'client-access' }" class="ml-2">Back to Client Access</router-link>
            </vs-alert>

            <div v-if="pageTitle" class="router-header flex flex-wrap items-center mb-6">
              <div class="content-area__heading">
                <h2 class="mb-1">{{ pageTitle }}</h2>
              </div>
            </div>

            <div class="content-area__content">
              <div v-if="loading" class="cp-state">
                <feather-icon icon="LoaderIcon" svgClasses="h-6 w-6" class="cp-spin" />
                <div>{{ t('loading') }}</div>
              </div>
              <div v-else-if="loadError" class="cp-state">
                <vs-alert color="danger" active="true">{{ loadError }}</vs-alert>
                <vs-button size="small" class="mt-4" @click="load">{{ t('retry') }}</vs-button>
              </div>
              <router-view v-else :key="$route.fullPath" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Gabarit du portail client (/client, rôle « client ») : reprend l'habillage de
// l'ERP (layouts/main/Main.vue : menu latéral, barre flottante, en-tête de
// page) SANS ses modules (recherche, appels, zone restreinte…) qui appellent
// des routes fermées à ce rôle. Charge le compte (/api/portal/me) et le
// partage aux pages (inject: ['portal']).
import VNavMenu from '@/layouts/components/vertical-nav-menu/VerticalNavMenu.vue'
import { portalGet, errorMessage } from '@/views/client/portalApi.js'
import { translate } from '@/views/client/i18n.js'

const SESSION_KEYS = [
  'accessToken', 'login-id', 'login-username', 'login-avatar', 'login-email', 'login-roles',
  'login-language', 'login-broker', 'login-date', 'login-readonly',
  'xylo:must-enable-2fa', 'xylo:twofa-status', 'xylo:twofa-setup-required'
]

const MENU = [
  { name: 'client-orders', path: '/client', label: 'nav_dashboard', icon: 'HomeIcon' },
  { name: 'client-quotations', path: '/client/quotations', label: 'nav_quotations', icon: 'FileIcon' },
  { name: 'client-invoices', path: '/client/invoices', label: 'nav_invoices', icon: 'FileTextIcon' },
  { name: 'client-account', path: '/client/account', label: 'nav_account', icon: 'UserIcon' }
]

export default {
  name: 'ClientLayout',
  components: { VNavMenu },
  provide () {
    return { portal: this.portal }
  },
  data () {
    return {
      portal: {
        me: null,
        lang: 'en',
        as: this.$route.query.as || null
      },
      loading: true,
      loadError: null
    }
  },
  computed: {
    menuItems () {
      const q = this.portal.as ? `?as=${encodeURIComponent(this.portal.as)}` : ''
      return MENU.map(m => {
        const name = this.t(m.label)
        return { url: `${m.path}${q}`, name, slug: m.name, icon: m.icon, i18n: name }
      })
    },
    pageTitle () {
      const key = this.$route.meta && this.$route.meta.portalTitle
      return key ? this.t(key) : ''
    },
    verticalNavMenuWidth () { return this.$store.state.verticalNavMenuWidth },
    contentAreaClass () {
      if (this.verticalNavMenuWidth === 'default') return 'content-area-reduced'
      if (this.verticalNavMenuWidth === 'reduced') return 'content-area-lg'
      return 'content-area-full'
    },
    navbarWidthClass () {
      if (this.verticalNavMenuWidth === 'default') return 'navbar-default'
      if (this.verticalNavMenuWidth === 'reduced') return 'navbar-reduced'
      return 'navbar-full'
    }
  },
  methods: {
    t (key, vars) {
      return translate(this.portal.lang, key, vars)
    },
    showSidebar () {
      this.$store.commit('TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE', true)
    },
    async load () {
      this.loading = true
      this.loadError = null
      try {
        const me = await portalGet('/me', this.portal.as)
        this.portal.me = me
        this.portal.lang = me.lang || 'en'
        document.title = 'XYLO'
      } catch (err) {
        this.loadError = await errorMessage(err, this.t('load_error'))
      } finally {
        this.loading = false
      }
    },
    signOut () {
      SESSION_KEYS.forEach(k => localStorage.removeItem(k))
      this.$router.push('/pages/login').catch(() => {})
    }
  },
  created () {
    // Même règle que Main.vue (setNavMenuVisibility) : menu replié sous 1200 px.
    if (this.$store.state.windowWidth < 1200) {
      this.$store.commit('TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE', false)
      this.$store.dispatch('updateVerticalNavMenuWidth', 'no-nav-menu')
    } else {
      this.$store.commit('TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE', true)
    }
    this.load()
  }
}
</script>

<style scoped>
.cp-companies {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
}
.cp-user {
  text-align: right;
  line-height: 1.2;
}
.cp-user-name {
  font-weight: 600;
}
.cp-user-mail {
  color: #94a3b8;
  font-size: 0.75rem;
}
</style>

<style>
/* Classes partagées par les pages du portail (views/client/*), préfixe cp-. */
.cp-layout .cp-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 3rem 1rem;
  color: #64748b;
  text-align: center;
}
.cp-layout .cp-spin {
  animation: cp-spin 1s linear infinite;
}
/* Hors du bloc scoped : vue-loader renommerait l'animation. */
@keyframes cp-spin {
  to {
    transform: rotate(360deg);
  }
}
.cp-layout .cp-muted {
  color: #94a3b8;
}
.cp-layout .cp-row-link {
  cursor: pointer;
}
.cp-layout .cp-link {
  cursor: pointer;
  color: rgba(var(--vs-primary), 1);
  font-weight: 600;
}
.cp-layout .con-vs-chip {
  white-space: nowrap;
}
.cp-layout .cp-facts {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 14px 24px;
}
.cp-layout .cp-fact-label {
  color: #94a3b8;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.cp-layout .cp-fact-value {
  font-weight: 600;
}
.cp-layout .cp-totals {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  margin-top: 14px;
}
.cp-layout .cp-totals .strong {
  font-size: 1.1rem;
  font-weight: 700;
}
.cp-layout .cp-num {
  text-align: right;
  white-space: nowrap;
}
</style>
